import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { customerApi, formatApiError } from '@/lib/api';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ConsentCheckboxes } from '@/components/customer/ConsentCheckboxes';
import { toast } from 'sonner';

// Top-Up mirrors the new-customer registration plan step: pick a plan + add-ons with live pricing.
// The only difference is the customer's CURRENT plan is marked "(current)" and pre-selected.
export function TopUpDialog({ open, onOpenChange, currentPlanId }) {
  const navigate = useNavigate();
  const [plans, setPlans] = useState(null);
  const [items, setItems] = useState([]);
  const [planId, setPlanId] = useState(currentPlanId || '');
  const [itemIds, setItemIds] = useState([]);
  const [discountInput, setDiscountInput] = useState('');
  const [appliedDiscount, setAppliedDiscount] = useState('');
  const [quote, setQuote] = useState(null);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [allergyOk, setAllergyOk] = useState(false);
  const [termsOk, setTermsOk] = useState(false);

  useEffect(() => {
    if (!open) return;
    customerApi
      .get('/registration/options')
      .then(({ data }) => {
        setPlans(data.plans);
        setItems(data.items);
        setPlanId((prev) => prev || data.plans.find((p) => p.id === currentPlanId)?.id || data.plans[0]?.id || '');
      })
      .catch(() => setPlans([]));
  }, [open, currentPlanId]);

  useEffect(() => {
    if (!open || !planId) return;
    setQuoteLoading(true);
    const t = setTimeout(() => {
      customerApi
        .post('/registration/quote', { planId, itemIds, discountCode: appliedDiscount || undefined })
        .then(({ data }) => setQuote(data))
        .catch(() => setQuote(null))
        .finally(() => setQuoteLoading(false));
    }, 200);
    return () => clearTimeout(t);
  }, [open, planId, itemIds, appliedDiscount]);

  const toggleItem = (id) => setItemIds((prev) => (prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]));

  const payOnline = async () => {
    if (!planId) return;
    setBusy(true);
    try {
      const { data } = await customerApi.post('/subscriptions/me/renew', {
        planId,
        itemIds,
        discountCode: appliedDiscount || undefined,
        acceptedAllergy: allergyOk,
        acceptedTerms: termsOk,
      });
      onOpenChange(false);
      navigate(`/pay/${data.payment.id}`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const totalLabel = useMemo(
    () => (quote ? `$${quote.finalAmount.toFixed(2)}` : '$0.00'),
    [quote],
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="topup-dialog" className="max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Renew Your Subscription</DialogTitle>
          <DialogDescription>
            Choose a plan to start fresh — add any per-tiffin add-ons and a discount code too. Your new tiffins and
            validity begin as soon as payment is confirmed.
          </DialogDescription>
        </DialogHeader>

        {plans === null ? (
          <p className="text-sm text-muted-ink py-4">Loading plans…</p>
        ) : plans.length === 0 ? (
          <p className="text-sm text-muted-ink py-4">No plans are available right now.</p>
        ) : (
          <div className="space-y-4">
            <RadioGroup value={planId} onValueChange={setPlanId} className="space-y-2" data-testid="topup-plan-options">
              {plans.map((plan) => (
                <label
                  key={plan.id}
                  htmlFor={`topup-plan-${plan.id}`}
                  data-testid={`topup-plan-option-${plan.id}`}
                  className={`flex items-start gap-3 rounded-xl border p-3 cursor-pointer transition-colors duration-150 ${
                    planId === plan.id ? 'border-coriander bg-coriander/5' : 'border-border/50'
                  }`}
                >
                  <RadioGroupItem value={plan.id} id={`topup-plan-${plan.id}`} className="mt-1" />
                  <div className="flex-1">
                    <div className="flex items-baseline justify-between">
                      <p className="text-sm font-semibold text-charcoal">
                        {plan.name} {plan.id === currentPlanId && <span className="text-xs text-coriander">(current)</span>}
                      </p>
                      <p className="font-heading font-700 text-charcoal">${Number(plan.price).toFixed(2)}</p>
                    </div>
                    <p className="text-xs text-muted-ink mt-0.5">{plan.tiffinCount} tiffins · {plan.validityDays} days</p>
                    {plan.description && <p className="text-xs text-muted-ink mt-1">{plan.description}</p>}
                  </div>
                </label>
              ))}
            </RadioGroup>

            {items.length > 0 && (
              <div>
                <p className="text-sm font-medium text-charcoal mb-2">Add-ons (optional) <span className="text-xs text-muted-ink font-normal">— priced per tiffin</span></p>
                <div className="space-y-2">
                  {items.map((it) => (
                    <label
                      key={it.id}
                      data-testid={`topup-addon-option-${it.id}`}
                      className="flex items-center gap-3 rounded-xl border border-border/40 p-3 cursor-pointer"
                    >
                      <Checkbox
                        data-testid={`topup-addon-checkbox-${it.id}`}
                        checked={itemIds.includes(it.id)}
                        onCheckedChange={() => toggleItem(it.id)}
                      />
                      <div className="flex-1 flex items-baseline justify-between">
                        <p className="text-sm text-charcoal">{it.name}</p>
                        <p className="text-sm font-medium text-charcoal">${Number(it.price).toFixed(2)} <span className="text-muted-ink font-normal text-xs">/ tiffin</span></p>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            )}

            <div className="space-y-1.5">
              <Label htmlFor="topup-discount">Discount Code</Label>
              <div className="flex gap-2">
                <Input
                  id="topup-discount"
                  data-testid="topup-discount-code-input"
                  value={discountInput}
                  onChange={(e) => setDiscountInput(e.target.value)}
                  placeholder="e.g. WELCOME10"
                />
                <Button type="button" variant="outline" data-testid="topup-apply-discount-btn" onClick={() => setAppliedDiscount(discountInput.trim())}>
                  Apply
                </Button>
              </div>
              {quote?.discountError && appliedDiscount && (
                <p className="text-xs text-destructive" data-testid="topup-discount-error">{quote.discountError}</p>
              )}
              {quote && !quote.discountError && quote.discountAmount > 0 && (
                <p className="text-xs text-coriander" data-testid="topup-discount-applied">
                  Discount applied: -${quote.discountAmount.toFixed(2)}
                </p>
              )}
            </div>

            {quote && (
              <div
                className={`rounded-xl bg-bone p-4 space-y-1.5 text-sm transition-opacity duration-150 ${quoteLoading ? 'opacity-50' : ''}`}
                data-testid="topup-summary-card"
              >
                <div className="flex justify-between">
                  <span className="text-muted-ink">Plan: {quote.plan.name}</span>
                  <span className="text-charcoal">${Number(quote.plan.price).toFixed(2)}</span>
                </div>
                {quote.items.map((it) => (
                  <div key={it.id} className="flex justify-between">
                    <span className="text-muted-ink">{it.name} <span className="text-xs">(${Number(it.unitPrice).toFixed(2)}/tiffin × {quote.plan.tiffinCount})</span></span>
                    <span className="text-charcoal">${Number(it.amount).toFixed(2)}</span>
                  </div>
                ))}
                <div className="flex justify-between pt-1 border-t border-border/40">
                  <span className="text-muted-ink">Subtotal</span>
                  <span className="text-charcoal">${quote.subtotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-ink">Discount</span>
                  <span className="text-charcoal">-${quote.discountAmount.toFixed(2)}</span>
                </div>
                <div className="flex justify-between pt-1 border-t border-border/40">
                  <span className="font-semibold text-charcoal">Total Due</span>
                  <span className="font-heading font-700 text-lg text-charcoal" data-testid="topup-total">{totalLabel}</span>
                </div>
              </div>
            )}

            <div className="border-t border-border/40 pt-3">
              <ConsentCheckboxes
                allergyChecked={allergyOk}
                termsChecked={termsOk}
                onAllergyChange={setAllergyOk}
                onTermsChange={setTermsOk}
              />
            </div>

            <DialogFooter>
              <Button
                data-testid="topup-pay-online-btn"
                disabled={busy || quoteLoading || !quote || !allergyOk || !termsOk}
                onClick={payOnline}
                className="w-full h-11 rounded-full bg-saffron hover:bg-saffron-hover transition-colors duration-150"
              >
                {busy ? 'Starting payment…' : `Renew — ${totalLabel}`}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
